<script setup>
import { nextTick, watch, ref, computed } from "vue";
import { useGlobalStore } from "../store/global";
import { IonToolbar, IonButtons, IonButton } from "@ionic/vue";

const store = useGlobalStore();

/* -------------------------------------------------- */
/*  Mask-coördinaten                                  */
/* -------------------------------------------------- */
const maskX = ref(0);
const maskY = ref(0);
const maskWidth = ref(0);
const maskHeight = ref(0);

/* -------------------------------------------------- */
/*  Highlight & popover                               */
/* -------------------------------------------------- */
const highlightElement = async (element) => {
  store.showOverlay = false;
  setTimeout(async () => {
    await nextTick();
    const rect = element.getBoundingClientRect();
    maskX.value      = rect.left  - 35;
    maskY.value      = rect.top   - 35;
    maskWidth.value  = rect.width + 70;
    maskHeight.value = rect.height + 70;
    store.showOverlay = true;
  }, 50);
};

const showGuidePopover = (element, text) => {
  store.guidePopover.visible = false;

  setTimeout(() => {
    const rect = element.getBoundingClientRect();
    const padding       = 16;
    const popoverWidth  = 400;
    const popoverHeight = 220;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top, left, position;

    if (rect.bottom + popoverHeight + padding < vh) {
      top = rect.bottom + padding + 20;
      left = rect.left + rect.width / 2 - popoverWidth / 2;
      position = "bottom";
    } else if (rect.top - popoverHeight - padding > 0) {
      top = rect.top - popoverHeight - padding - 10;
      left = rect.left + rect.width / 2 - popoverWidth / 2;
      position = "top";
    } else if (rect.right + popoverWidth + padding < vw) {
      top = rect.top + rect.height / 2 - popoverHeight / 2;
      left = rect.right + padding;
      position = "right";
    } else if (rect.left - popoverWidth - padding > 0) {
      top = rect.top + rect.height / 2 - popoverHeight / 2;
      left = rect.left - popoverWidth - padding;
      position = "left";
    } else {
      top = vh / 2 - popoverHeight / 2;
      left = vw / 2 - popoverWidth / 2;
      position = "center";
    }

    /* klemmen binnen viewport */
    left = Math.max(8, Math.min(left, vw - popoverWidth  - 8));
    top  = Math.max(8, Math.min(top , vh - popoverHeight - 8));

    Object.assign(store.guidePopover, {
      text,
      top : `${top}px`,
      left: `${left}px`,
      visible : true,
      position
    });
  }, 50);
};

/* -------------------------------------------------- */
/*  Navigatie-knoppen                                 */
/* -------------------------------------------------- */
function nextElement() {
  if (store.activeElement < store.activeGuide.length - 1) {
    store.activeElement++;
  }
}
function previousElement() {
  if (store.activeElement > 0) {
    store.activeElement--;
  }
}

/* -------------------------------------------------- */
/*  Computed helpers                                  */
/* -------------------------------------------------- */
const currentStep = computed(() => store.activeGuide?.[store.activeElement]);

const currentConditionResult = computed(() => {
  const step = currentStep.value;
  return typeof step?.condition === "function" ? step.condition(store) : false;
});

/* -------------------------------------------------- */
/*  Watchers                                          */
/* -------------------------------------------------- */
/* 1. Reageer op stap-wijziging                       */
watch(currentStep, async (step) => {
  if (!step) return;

  /* ⏩  Als de stap alleen een next_guide bevat → wissel gids */
  if (step.next_guide) {
    const newGuide = store[step.next_guide];
    if (newGuide) {
      /* fade-out huidige overlay & popover */
      store.showOverlay = false;
      store.guidePopover.visible = false;
      setTimeout(() => {
        store.activeGuide   = newGuide;
        store.activeElement = 0;
      }, 300);
    }
    return; // geen highlight voor deze stap
  }

  /* normale stap met element-key -------------------------------- */
  if (!step.element) return;
  const refEntry = store.refRegistry[step.element];
  const el = refEntry?.$el ?? refEntry?.value ?? refEntry; // normaliseren
  if (!el) return;

  await nextTick();
  highlightElement(el);
  showGuidePopover(el, step.text);
});

/* 2. Auto-advance wanneer condition true wordt        */
watch(currentConditionResult, (fulfilled) => {
  if (fulfilled && store.activeElement < store.activeGuide.length - 1) {
    /* fade-out voordat we ++ doen */
    store.showOverlay         = false;
    store.guidePopover.visible = false;
    setTimeout(() => {
      store.activeElement++;
    }, 500);
  }
});

function exitGuide() {
  window.location.href = window.location.origin;
}
</script>

<template>
  <!-- BACKDROP --------------------------------------------------->
  <transition name="fade">
    <div v-if="store.showOverlay" class="guide-backdrop-svg">
      <svg>
        <defs>
          <filter id="blur"><feGaussianBlur in="SourceGraphic" stdDeviation="8"/></filter>
          <mask id="mask">
            <rect width="100%" height="100%" fill="white"/>
            <g filter="url(#blur)">
              <rect :x="maskX" :y="maskY"
                    :width="maskWidth" :height="maskHeight"
                    rx="8" ry="8" fill="black"/>
            </g>
          </mask>
        </defs>
        <rect width="100%" height="100%"
              fill="rgba(15,15,33,0.4)"
              mask="url(#mask)"/>
      </svg>
    </div>
  </transition>

  <!-- POPOVER ----------------------------------------------------->
  <transition name="fade">
    <div v-if="store.guidePopover.visible"
         class="guide-popover"
         :class="`position-${store.guidePopover.position}`"
         :style="{ top: store.guidePopover.top, left: store.guidePopover.left }">

      <ion-toolbar class="controls">
        <ion-buttons slot="start">
          <ion-button @click="exitGuide()" class="skip" style="--color:var(--ion-color-light)">Skip tour</ion-button>
        </ion-buttons>
        <ion-buttons slot="end">
          <!-- Toon “Next” enkel als er géén condition is -->
          <ion-button v-if="!currentStep?.condition"
                      @click="nextElement"
                      style="--color:white">
            Next
          </ion-button>
        </ion-buttons>
      </ion-toolbar>

      <div class="arrow"></div>
      <div class="content">{{ store.guidePopover.text }}</div>
    </div>
  </transition>
</template>

<style>
/* fade-transitie */
.fade-enter-active, .fade-leave-active { transition: opacity .3s ease; }
.fade-enter-from,   .fade-leave-to      { opacity: 0; }
.fade-enter-to,     .fade-leave-from    { opacity: 1; }

/* backdrop */
.guide-backdrop-svg { position: fixed; inset: 0; z-index: 98; pointer-events: none; }
svg { width: 100%; height: 100%; }

/* popover */
.guide-popover {
  position: absolute;
  background: #f5f5f5;
  width: 400px;
  border-radius: 12px;
  box-shadow: 0 0 24px rgba(0,0,0,.5);
  z-index: 1000;
}
.arrow { width: 0; height: 0; position: absolute; }
/* pijlen */
.position-bottom .arrow { top:-10px; left:50%; transform:translateX(-50%);
  border-left:8px solid transparent; border-right:8px solid transparent;
  border-bottom:10px solid #f5f5f5; }
.position-top .arrow { bottom:-10px; left:50%; transform:translateX(-50%);
  border-left:8px solid transparent; border-right:8px solid transparent;
  border-top:10px solid #f5f5f5; }
.position-right .arrow { left:-10px; top:50%; transform:translateY(-50%);
  border-top:8px solid transparent; border-bottom:8px solid transparent;
  border-right:10px solid #f5f5f5; }
.position-left .arrow { right:-10px; top:50%; transform:translateY(-50%);
  border-top:8px solid transparent; border-bottom:8px solid transparent;
  border-left:10px solid #f5f5f5; }

.content { padding: 16px; font-size: 16px; }

.controls {
  --background: rgba(216,124,31,.8);
  display:flex; justify-content:space-between;
  border-top-left-radius:12px; border-top-right-radius:12px;
}
.skip { background:none; border:none; color:#888; font-size:16px; }
</style>